import { Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { MenuButton } from "./MenuButton";
import {
  NavItem,
  type NavItem as NavItemType,
  type NavItemWithSubmenu,
} from "./NavItem";
import { AuthButtons } from "./AuthButtons";
import { motion, AnimatePresence } from "framer-motion";
import { House, GithubLogo, Article, Users } from "@phosphor-icons/react";

export function Navigation() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const navItems: (NavItemType | NavItemWithSubmenu)[] = [
    { href: "/app", label: "Home", icon: House },
    ...(user
      ? [
          {
            label: "GitHub",
            href: "/app/github/settings",
            icon: GithubLogo,
          },
          {
            label: "Posts",
            href: "/app/posts",
            icon: Article,
          },
          ...(user.roles?.some((role) => ["master", "admin"].includes(role))
            ? [{ href: "/app/users", label: "Users", icon: Users }]
            : []),
        ]
      : []),
  ];

  const toggleSubmenu = (label: string) => {
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  const handleNavClick = () => {
    setIsOpen(false);
  };

  const NavigationContent = () => (
    <div className="h-full flex flex-col relative">
      {/* Logo - only show on desktop */}
      <div className="hidden lg:flex h-12 items-center px-3">
        <Link to="/app" className="flex items-center gap-1.5 group">
          <img
            src="/acr-logo.svg"
            alt="AI Code Review Logo"
            className="h-6 w-auto transition-transform group-hover:scale-110"
          />
          <span className="text-base font-medium bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">
            AI Code Review
          </span>
        </Link>
      </div>

      {/* Navigation items - add top padding on mobile */}
      <div className="flex-1 py-2 px-2 overflow-y-auto lg:pt-2 pt-16">
        <nav className="nav-menu space-y-0.5">
          {navItems.map((item) => (
            <NavItem
              key={item.label}
              item={item}
              openSubmenu={openSubmenu}
              toggleSubmenu={toggleSubmenu}
              onNavClick={handleNavClick}
            />
          ))}
          <div className="nav-divider my-2" />
          <AuthButtons onNavClick={handleNavClick} />
        </nav>
      </div>

      {/* Aurora effect */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute -inset-[10px] opacity-20">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-[#8B5CF6] rounded-full mix-blend-multiply filter blur-[128px] animate-aurora" />
          <div className="absolute bottom-0 -right-4 w-96 h-96 bg-[#3B82F6] rounded-full mix-blend-multiply filter blur-[128px] animate-aurora delay-[2s]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#EC4899] rounded-full mix-blend-multiply filter blur-[128px] animate-aurora delay-[4s]" />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile header - highest z-index */}
      <div className="lg:hidden fixed top-0 left-0 right-0 backdrop-blur-2xl bg-[#030014]/90 border-b border-white/[0.08] z-[100]">
        <div className="px-4 h-14 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2 group">
            <img
              src="/acr-logo.svg"
              alt="AI Code Review Logo"
              className="h-7 w-auto transition-transform group-hover:scale-110"
            />
            <span className="text-base font-medium bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">
              AI Code Review
            </span>
          </Link>
          <MenuButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} />
        </div>
      </div>

      {/* Mobile Navigation (with animation) */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop - lower z-index than header */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-md z-[80]"
              onClick={() => setIsOpen(false)}
            />

            {/* Navigation - between backdrop and header */}
            <motion.nav
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="lg:hidden fixed inset-y-0 left-0 w-48 bg-[#030014]/95 backdrop-blur-2xl border-r border-white/[0.08] shadow-2xl shadow-black/20 z-[90]"
            >
              <NavigationContent />
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Navigation */}
      <nav className="hidden lg:block fixed inset-y-0 left-0 w-48 bg-[#030014]/95 backdrop-blur-2xl border-r border-white/[0.08] shadow-2xl shadow-black/20 z-[100]">
        <NavigationContent />
      </nav>
    </>
  );
}
